import { useState, useEffect, useRef } from "react";
import { AudioMP3 } from "@/entities/AudioMP3";
import { Music, Search, ChevronDown, Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, Repeat, Shuffle } from "lucide-react";

const GENEROS = ["todos", "pop", "rock", "reggaeton", "salsa", "cumbia", "vallenato", "electronica", "clasica", "otro"];

export default function AudiosMP3() {
  const [audios, setAudios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [audioActivo, setAudioActivo] = useState(null);
  const [reproduciendo, setReproduciendo] = useState(false);
  const [progreso, setProgreso] = useState(0);
  const [duracionActual, setDuracionActual] = useState(0);
  const [duracionTotal, setDuracionTotal] = useState(0);
  const [volumen, setVolumen] = useState(0.8);
  const [silenciado, setSilenciado] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [genero, setGenero] = useState("todos");
  const [repetir, setRepetir] = useState(false);
  const [aleatorio, setAleatorio] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    async function cargar() {
      const data = await AudioMP3.filter({ activo: true }, "orden", 100);
      setAudios(data);
      if (data.length > 0) {
        const urlParams = new URLSearchParams(window.location.search);
        const aid = urlParams.get("audio");
        const found = aid ? data.find(a => a.id === aid) : null;
        setAudioActivo(found || data[0]);
      }
      setLoading(false);
    }
    cargar();
  }, []);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = silenciado ? 0 : volumen;
    }
  }, [volumen, silenciado]);

  const filtrados = audios.filter(a => {
    const matchB = a.titulo?.toLowerCase().includes(busqueda.toLowerCase()) ||
      a.artista?.toLowerCase().includes(busqueda.toLowerCase());
    const matchG = genero === "todos" || a.genero === genero;
    return matchB && matchG;
  });

  const reproducirAudio = (audio) => {
    setAudioActivo(audio);
    setReproduciendo(true);
    setTimeout(() => audioRef.current?.play(), 100);
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (reproduciendo) {
      audioRef.current.pause();
      setReproduciendo(false);
    } else {
      audioRef.current.play();
      setReproduciendo(true);
    }
  };

  const anterior = () => {
    const idx = audios.findIndex(a => a.id === audioActivo?.id);
    if (idx > 0) reproducirAudio(audios[idx - 1]);
  };

  const siguiente = () => {
    if (aleatorio) {
      const idx = Math.floor(Math.random() * audios.length);
      reproducirAudio(audios[idx]);
      return;
    }
    const idx = audios.findIndex(a => a.id === audioActivo?.id);
    if (idx < audios.length - 1) reproducirAudio(audios[idx + 1]);
    else if (repetir) reproducirAudio(audios[0]);
  };

  const onTimeUpdate = () => {
    if (!audioRef.current) return;
    setDuracionActual(audioRef.current.currentTime);
    setDuracionTotal(audioRef.current.duration || 0);
    setProgreso((audioRef.current.currentTime / (audioRef.current.duration || 1)) * 100);
  };

  const onEnded = () => {
    if (repetir) {
      audioRef.current?.play();
    } else {
      siguiente();
    }
  };

  const seekTo = (e) => {
    if (!audioRef.current || !duracionTotal) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = x / rect.width;
    audioRef.current.currentTime = pct * duracionTotal;
  };

  const formatTime = (s) => {
    if (!s || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  return (
    <div className="min-h-screen bg-black text-white pb-36">
      {/* Audio element oculto */}
      {audioActivo?.url_audio && (
        <audio
          ref={audioRef}
          src={audioActivo.url_audio}
          onTimeUpdate={onTimeUpdate}
          onEnded={onEnded}
          onPlay={() => setReproduciendo(true)}
          onPause={() => setReproduciendo(false)}
          autoPlay={reproduciendo}
        />
      )}

      <div className="border-b border-yellow-500/20 px-4 py-4">
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
            <Music className="w-4 h-4 text-green-400" />
          </div>
          <h1 className="text-xl font-bold text-white">Audios MP3</h1>
          <span className="text-gray-500 text-sm">{audios.length} pistas</span>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Portada del audio activo */}
        {audioActivo && (
          <div className="flex flex-col sm:flex-row items-center gap-6 mb-8 p-5 bg-gray-950 rounded-2xl border border-yellow-500/20">
            <div className="w-32 h-32 rounded-2xl overflow-hidden flex-shrink-0 bg-gradient-to-br from-green-900 to-gray-800 flex items-center justify-center shadow-xl shadow-green-900/30">
              {audioActivo.imagen_portada ? (
                <img src={audioActivo.imagen_portada} alt={audioActivo.titulo} className="w-full h-full object-cover" />
              ) : (
                <Music className="w-12 h-12 text-green-400" />
              )}
            </div>
            <div className="flex-1 text-center sm:text-left">
              <p className="text-xs text-gray-500 uppercase tracking-widest mb-1">Reproduciendo ahora</p>
              <h2 className="text-2xl font-black text-white">{audioActivo.titulo}</h2>
              <p className="text-yellow-400 font-semibold mt-0.5">{audioActivo.artista || "Artista desconocido"}</p>
              {audioActivo.album && <p className="text-gray-500 text-sm mt-0.5">{audioActivo.album}</p>}
              <span className="inline-block mt-2 text-xs bg-gray-800 text-gray-400 px-2 py-0.5 rounded-full capitalize">{audioActivo.genero}</span>
            </div>
          </div>
        )}

        {/* Filtros */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Buscar canción o artista..."
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
              className="w-full bg-gray-900 border border-gray-800 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-yellow-500/50"
            />
          </div>
          <div className="relative sm:w-48">
            <select
              value={genero}
              onChange={e => setGenero(e.target.value)}
              className="w-full bg-gray-900 border border-gray-800 rounded-xl px-4 py-2.5 text-sm text-white appearance-none focus:outline-none focus:border-yellow-500/50"
            >
              {GENEROS.map(g => (
                <option key={g} value={g}>{g === "todos" ? "Todos los géneros" : g}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
          </div>
        </div>

        {/* Lista */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : audios.length === 0 ? (
          <div className="text-center py-20 text-gray-600">
            <Music className="w-16 h-16 mx-auto mb-4 opacity-20" />
            <p className="text-lg">No hay audios disponibles</p>
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            {filtrados.map((audio, idx) => {
              const activo = audioActivo?.id === audio.id;
              return (
                <button
                  key={audio.id}
                  onClick={() => reproducirAudio(audio)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-left w-full group ${
                    activo ? "bg-yellow-500/10 border border-yellow-500/40" : "hover:bg-gray-900 border border-transparent"
                  }`}
                >
                  <span className={`w-6 text-center text-sm flex-shrink-0 ${activo ? "text-yellow-400" : "text-gray-600 group-hover:hidden"}`}>
                    {activo && reproduciendo ? (
                      <span className="flex gap-0.5 items-end h-4 justify-center">
                        <span className="w-0.5 bg-yellow-400 animate-bounce" style={{height:"60%", animationDelay:"0ms"}} />
                        <span className="w-0.5 bg-yellow-400 animate-bounce" style={{height:"100%", animationDelay:"150ms"}} />
                        <span className="w-0.5 bg-yellow-400 animate-bounce" style={{height:"40%", animationDelay:"300ms"}} />
                      </span>
                    ) : (
                      idx + 1
                    )}
                  </span>
                  <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-gray-800 flex items-center justify-center">
                    {audio.imagen_portada ? (
                      <img src={audio.imagen_portada} alt={audio.titulo} className="w-full h-full object-cover" />
                    ) : (
                      <Music className="w-4 h-4 text-gray-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-semibold text-sm truncate ${activo ? "text-yellow-400" : "text-white"}`}>{audio.titulo}</p>
                    <p className="text-gray-500 text-xs truncate">{audio.artista || "Desconocido"} {audio.album ? `· ${audio.album}` : ""}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-600 capitalize hidden sm:block">{audio.genero}</span>
                    {audio.duracion && <span className="text-xs text-gray-600">{audio.duracion}</span>}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Reproductor fijo en la parte inferior */}
      {audioActivo && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-gray-950 border-t border-yellow-500/30 px-4 py-3">
          <div className="max-w-5xl mx-auto">
            {/* Barra de progreso */}
            <div
              className="w-full h-1.5 bg-gray-800 rounded-full mb-3 cursor-pointer group"
              onClick={seekTo}
            >
              <div
                className="h-full bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full relative transition-all"
                style={{ width: `${progreso}%` }}
              >
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-yellow-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Info */}
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-gray-800 flex items-center justify-center">
                  {audioActivo.imagen_portada ? (
                    <img src={audioActivo.imagen_portada} alt={audioActivo.titulo} className="w-full h-full object-cover" />
                  ) : (
                    <Music className="w-4 h-4 text-gray-600" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-white text-sm font-semibold truncate">{audioActivo.titulo}</p>
                  <p className="text-gray-500 text-xs truncate">{audioActivo.artista || "Desconocido"}</p>
                </div>
              </div>

              {/* Controles */}
              <div className="flex items-center gap-2">
                <button onClick={() => setAleatorio(!aleatorio)}
                  className={`p-1.5 rounded-lg transition-colors ${aleatorio ? "text-yellow-400" : "text-gray-600 hover:text-gray-400"}`}>
                  <Shuffle className="w-4 h-4" />
                </button>
                <button onClick={anterior}
                  className="p-2 text-gray-400 hover:text-white transition-colors">
                  <SkipBack className="w-5 h-5" />
                </button>
                <button onClick={togglePlay}
                  className="w-10 h-10 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 flex items-center justify-center hover:scale-105 transition-transform shadow-lg shadow-yellow-500/30">
                  {reproduciendo ? <Pause className="w-5 h-5 text-black" /> : <Play className="w-5 h-5 text-black ml-0.5" />}
                </button>
                <button onClick={siguiente}
                  className="p-2 text-gray-400 hover:text-white transition-colors">
                  <SkipForward className="w-5 h-5" />
                </button>
                <button onClick={() => setRepetir(!repetir)}
                  className={`p-1.5 rounded-lg transition-colors ${repetir ? "text-yellow-400" : "text-gray-600 hover:text-gray-400"}`}>
                  <Repeat className="w-4 h-4" />
                </button>
              </div>

              {/* Tiempo y volumen */}
              <div className="hidden sm:flex items-center gap-2 flex-1 justify-end">
                <span className="text-xs text-gray-500">{formatTime(duracionActual)} / {formatTime(duracionTotal)}</span>
                <button onClick={() => setSilenciado(!silenciado)} className="text-gray-500 hover:text-white transition-colors">
                  {silenciado ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </button>
                <input
                  type="range" min="0" max="1" step="0.05"
                  value={silenciado ? 0 : volumen}
                  onChange={e => { setVolumen(parseFloat(e.target.value)); setSilenciado(false); }}
                  className="w-20 accent-yellow-400"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}